import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import expensesApi from '../api/expenses';
import { Expense, EXPENSE_STATUS_LABELS, EXPENSE_STATUS_COLORS } from '../types/expense';
import Pagination from '../components/Pagination';
import ConfirmDialog from '../components/ConfirmDialog';
import { useAuth } from '../contexts/AuthContext';

const LIMIT_OPTIONS = [10, 25, 50, 100];

const Trash: React.FC = () => {
  const { isOperator } = useAuth();
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    type: 'permanentDelete' | 'bulkDelete' | 'emptyTrash' | null;
    expenseId: string | null;
    expenseName: string | null;
  }>({ isOpen: false, type: null, expenseId: null, expenseName: null });

  const showMessage = (type: 'success' | 'error', text: string) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 3000);
  };

  const fetchTrash = useCallback(async (signal?: AbortSignal) => {
    setIsLoading(true);
    try {
      const response = await expensesApi.getTrashed(page, limit, signal);
      setExpenses(response.data);
      setTotalPages(response.totalPages);
      setTotal(response.total);
    } catch (error: any) {
      if (signal?.aborted) return;
      console.error('Error fetching trash:', error);
    } finally {
      setIsLoading(false);
    }
  }, [page, limit]);

  useEffect(() => {
    const abortController = new AbortController();
    fetchTrash(abortController.signal);
    return () => abortController.abort();
  }, [fetchTrash]);

  const handleLimitChange = (newLimit: number) => {
    setLimit(newLimit);
    setPage(1);
  };

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === expenses.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(expenses.map((e) => e.id)));
    }
  };

  const handleRestore = async (id: string) => {
    try {
      await expensesApi.restore(id);
      setExpenses((prev) => prev.filter((e) => e.id !== id));
      setTotal((prev) => prev - 1);
      setSelectedIds((prev) => { const next = new Set(prev); next.delete(id); return next; });
      showMessage('success', 'Запись восстановлена');
    } catch (error: any) {
      showMessage('error', error.response?.data?.message || 'Ошибка при восстановлении');
    }
  };

  const handlePermanentDelete = async (id: string) => {
    try {
      await expensesApi.permanentDelete(id);
      setExpenses((prev) => prev.filter((e) => e.id !== id));
      setTotal((prev) => prev - 1);
      setSelectedIds((prev) => { const next = new Set(prev); next.delete(id); return next; });
      showMessage('success', 'Запись удалена безвозвратно');
    } catch (error: any) {
      showMessage('error', error.response?.data?.message || 'Ошибка при удалении');
    }
  };

  const handleBulkRestore = async () => {
    if (selectedIds.size === 0) return;
    try {
      const result = await expensesApi.bulkRestore(Array.from(selectedIds));
      showMessage('success', `Восстановлено записей: ${result.restored}`);
      setSelectedIds(new Set());
      fetchTrash();
    } catch (error: any) {
      showMessage('error', error.response?.data?.message || 'Ошибка при восстановлении');
    }
  };

  const handleBulkPermanentDelete = async () => {
    try {
      const result = await expensesApi.bulkPermanentDelete(Array.from(selectedIds));
      showMessage('success', `Удалено безвозвратно: ${result.deleted}`);
      setSelectedIds(new Set());
      fetchTrash();
    } catch (error: any) {
      showMessage('error', error.response?.data?.message || 'Ошибка при удалении');
    }
  };

  const handleEmptyTrash = async () => {
    try {
      const result = await expensesApi.emptyTrash();
      showMessage('success', `Корзина очищена. Удалено: ${result.deleted}`);
      setSelectedIds(new Set());
      fetchTrash();
    } catch (error: any) {
      showMessage('error', error.response?.data?.message || 'Ошибка при очистке корзины');
    }
  };

  const handleDialogConfirm = () => {
    const { type, expenseId } = confirmDialog;
    setConfirmDialog({ isOpen: false, type: null, expenseId: null, expenseName: null });

    if (type === 'permanentDelete' && expenseId) {
      handlePermanentDelete(expenseId);
    } else if (type === 'bulkDelete') {
      handleBulkPermanentDelete();
    } else if (type === 'emptyTrash') {
      handleEmptyTrash();
    }
  };

  const closeDialog = () => {
    setConfirmDialog({ isOpen: false, type: null, expenseId: null, expenseName: null });
  };

  const formatDate = (dateString: string | null): string => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const getCurrencySymbol = (currency: string): string => {
    const symbols: Record<string, string> = {
      BYN: 'Br', EUR: '\u20AC', USD: '$', PLN: 'z\u0142', RUB: '\u20BD', UAH: '\u20B4', GBP: '\u00A3',
    };
    return symbols[currency] || currency;
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-white shadow">
        <div className="max-w-5xl mx-auto px-3 sm:px-6 lg:px-8 py-3 sm:py-4">
          <div className="flex items-center gap-3">
            <Link
              to="/dashboard"
              className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
              title="Назад"
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </Link>
            <h1 className="text-lg sm:text-2xl font-bold text-gray-800">Корзина</h1>
            <span className="text-sm text-gray-500 ml-auto">
              {total > 0 ? `${total} записей` : ''}
            </span>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-8">
        <div className="bg-white rounded-lg shadow-md p-3 sm:p-6">
          {/* Message */}
          {message && (
            <div className={`mb-4 p-2 text-sm rounded-md flex items-center justify-between ${
              message.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
            }`}>
              <span>{message.text}</span>
              <button onClick={() => setMessage(null)} className="p-0.5 hover:opacity-70">
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          )}

          {/* Bulk actions */}
          {(selectedIds.size > 0 || total > 0) && (
            <div className="mb-4 flex flex-wrap items-center gap-2">
              {selectedIds.size > 0 && (
                <>
                  <span className="text-sm text-gray-600">
                    Выбрано: {selectedIds.size}
                  </span>
                  <button
                    onClick={handleBulkRestore}
                    className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                  >
                    Восстановить выбранные
                  </button>
                  {!isOperator && (
                    <button
                      onClick={() => setConfirmDialog({ isOpen: true, type: 'bulkDelete', expenseId: null, expenseName: null })}
                      className="px-3 py-1.5 text-sm bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                    >
                      Удалить выбранные
                    </button>
                  )}
                </>
              )}
              {total > 0 && !isOperator && (
                <button
                  onClick={() => setConfirmDialog({ isOpen: true, type: 'emptyTrash', expenseId: null, expenseName: null })}
                  className="px-3 py-1.5 text-sm text-red-600 border border-red-300 rounded-md hover:bg-red-50 transition-colors ml-auto"
                >
                  Очистить корзину
                </button>
              )}
            </div>
          )}

          {isLoading ? (
            <p className="text-gray-500 text-center py-8">Загрузка...</p>
          ) : expenses.length === 0 ? (
            <p className="text-gray-500 text-center py-8">Корзина пуста</p>
          ) : (
            <div className="space-y-3">
              {/* Select all */}
              <label className="flex items-center gap-2 text-sm text-gray-500 cursor-pointer select-none">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  checked={expenses.length > 0 && selectedIds.size === expenses.length}
                  onChange={toggleSelectAll}
                />
                Выбрать все
              </label>

              {expenses.map((expense) => (
                <div
                  key={expense.id}
                  className={`p-3 sm:p-4 rounded-lg border bg-gray-50 border-gray-200 ${selectedIds.has(expense.id) ? 'ring-2 ring-blue-300' : ''} hover:shadow-md transition-shadow`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3 flex-1">
                      <input
                        type="checkbox"
                        className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 shrink-0"
                        checked={selectedIds.has(expense.id)}
                        onChange={() => toggleSelect(expense.id)}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 flex-wrap">
                          <span className="text-sm text-gray-500">{formatDate(expense.date)}</span>
                          <span className="font-medium text-gray-800">{expense.name}</span>
                        </div>
                        <div className="flex items-center gap-3 mt-2 flex-wrap">
                          <span className={`text-lg font-semibold ${expense.type === 'income' ? 'text-green-700' : 'text-red-700'}`}>
                            {expense.type === 'income' ? '+' : '\u2212'}{expense.amount} {getCurrencySymbol(expense.currency)}
                          </span>
                          {expense.type !== 'income' && (
                            <span className={`px-2 py-0.5 text-xs rounded-full ${EXPENSE_STATUS_COLORS[expense.status]}`}>
                              {EXPENSE_STATUS_LABELS[expense.status]}
                            </span>
                          )}
                          {expense.costCategory && (
                            <span className="px-2 py-0.5 text-xs bg-purple-100 text-purple-800 rounded-full">
                              {expense.costCategory}
                            </span>
                          )}
                        </div>
                        {expense.contractor && (
                          <div className="mt-1 text-sm text-gray-600">
                            Контрагент: {expense.contractor}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-2 ml-4">
                      <button
                        onClick={() => handleRestore(expense.id)}
                        className="p-2 text-blue-600 hover:bg-blue-100 rounded-md transition-colors"
                        title="Восстановить"
                      >
                        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                        </svg>
                      </button>
                      {!isOperator && (
                        <button
                          onClick={() => setConfirmDialog({
                            isOpen: true,
                            type: 'permanentDelete',
                            expenseId: expense.id,
                            expenseName: expense.name,
                          })}
                          className="p-2 text-red-600 hover:bg-red-100 rounded-md transition-colors"
                          title="Удалить безвозвратно"
                        >
                          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          <Pagination
            currentPage={page}
            totalPages={totalPages}
            onPageChange={setPage}
            limit={limit}
            limitOptions={LIMIT_OPTIONS}
            onLimitChange={handleLimitChange}
            total={total}
          />
        </div>
      </main>

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={
          confirmDialog.type === 'emptyTrash'
            ? 'Очистить корзину'
            : 'Удалить безвозвратно'
        }
        message={
          confirmDialog.type === 'emptyTrash'
            ? `Удалить все записи из корзины (${total})? Это действие нельзя отменить.`
            : confirmDialog.type === 'bulkDelete'
              ? `Удалить безвозвратно выбранные записи (${selectedIds.size})? Это действие нельзя отменить.`
              : `Удалить "${confirmDialog.expenseName}" безвозвратно? Это действие нельзя отменить.`
        }
        confirmText="Удалить"
        cancelText="Отмена"
        variant="danger"
        onConfirm={handleDialogConfirm}
        onCancel={closeDialog}
      />
    </div>
  );
};

export default Trash;
